import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { NewsArticle } from '@/services/newsroomApi';

const STATUS_STYLES: Record<NewsArticle['status'], string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  in_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  published: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

interface ArticleCardProps {
  article: NewsArticle;
}

// Articles published within this window get a "New" badge so the freshest
// feed items stand out regardless of which sort order the list is in.
const NEW_THRESHOLD_MS = 3 * 60 * 60 * 1000;

const ArticleCard: React.FC<ArticleCardProps> = ({ article }) => {
  const title = article.translatedTitle || article.originalTitle;
  const isNew = Date.now() - new Date(article.publishedAt).getTime() < NEW_THRESHOLD_MS;

  return (
    <Link
      to={`/app/newsroom/articles/${article._id}`}
      className="block rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{article.outlet}</span>
          {isNew && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-600 text-white">NEW</span>
          )}
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[article.status]}`}>
          {article.status.replace('_', ' ')}
        </span>
      </div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">{title}</h3>
      <div className="mt-2 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
        <span>{article.originalLanguage === 'ta' ? 'Tamil source' : 'English source'}</span>
        <span>{formatDistanceToNow(new Date(article.publishedAt), { addSuffix: true })}</span>
      </div>
    </Link>
  );
};

export default ArticleCard;
